import React, { useState } from 'react';
import {
  IconMessage,
  IconPatients,
  IconSend,
} from '../components/icons';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/Card';
import Button from '../components/Button';
import { Label, Input, Textarea, Select } from '../components/Input';
import Alert from '../components/Alert';
import PatientSelector from '../components/PatientSelector';
import { campaignAPI, formatPhoneNumber } from '../services/api';
import { useAuth } from '../context/AuthContext';

const SMSCampaign = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [selectedPatients, setSelectedPatients] = useState([]);

  const [formData, setFormData] = useState({
    campaignName: '',
    message: '',
    healthCondition: 'preeclampsia',
  });

  const healthConditions = [
    { value: 'preeclampsia', label: 'Preeclampsia / Maternal Health' },
    { value: 'mental_health', label: 'Mental Health' },
    { value: 'autism', label: 'Autism Spectrum' },
    { value: 'breast_cancer', label: 'Breast Cancer' },
    { value: 'malaria', label: 'Malaria Prevention' },
    { value: 'general', label: 'General Health' },
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);
    setLoading(true);

    try {
      if (selectedPatients.length === 0) {
        setAlert({ type: 'error', title: 'No patients selected', message: 'Please select at least one patient.' });
        setLoading(false);
        return;
      }

      // Normalize to 233XXXXXXXXX — correct format for mNotify SMS API
      const recipients = selectedPatients.map((p) => formatPhoneNumber(p.phone));

      const result = await campaignAPI.createSMSCampaign({
        campaign_name: formData.campaignName,
        recipients,
        message: formData.message,
        health_condition: formData.healthCondition,
        user_id: user?.id ?? null,
      });

      const failNote = result.failed > 0 ? ` (${result.failed} failed)` : '';
      setAlert({
        type: result.sent > 0 ? 'success' : 'error',
        title: result.sent > 0 ? 'Campaign Sent' : 'Send Failed',
        message:
          result.sent > 0
            ? `SMS sent to ${result.sent} of ${recipients.length} patient(s)${failNote}.`
            : 'No messages could be delivered. Check the backend connection.',
      });

      if (result.sent > 0) {
        setFormData({ campaignName: '', message: '', healthCondition: 'preeclampsia' });
        setSelectedPatients([]);
      }
    } catch (error) {
      setAlert({
        type: 'error',
        title: 'Campaign Failed',
        message: error.userMessage || error.response?.data?.detail || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">SMS Campaign</h2>
        <p className="text-gray-600 mt-1">
          IconSend health education messages via SMS to your patients
        </p>
      </div>

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Create SMS Campaign</CardTitle>
              <CardDescription>Compose and send health education messages to selected patients</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Campaign Name */}
                <div>
                  <Label htmlFor="campaignName">Campaign Name *</Label>
                  <Input
                    id="campaignName"
                    name="campaignName"
                    placeholder="e.g., Preeclampsia Awareness Campaign"
                    value={formData.campaignName}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Health Condition */}
                <div>
                  <Label htmlFor="healthCondition">Health Condition *</Label>
                  <Select
                    id="healthCondition"
                    name="healthCondition"
                    value={formData.healthCondition}
                    onChange={handleChange}
                    required
                  >
                    {healthConditions.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </Select>
                </div>

                {/* Patient Selector */}
                <div>
                  <Label>
                    Patients *{' '}
                    {selectedPatients.length > 0 && (
                      <span className="text-gray-500 font-normal">
                        ({selectedPatients.length} selected)
                      </span>
                    )}
                  </Label>
                  <div className="mt-1">
                    <PatientSelector
                      onSelect={setSelectedPatients}
                      title="Select patients to receive this SMS"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Phone numbers are normalized to 233XXXXXXXXX automatically.
                  </p>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message">
                    Message *{' '}
                    <span className="text-gray-500 font-normal">
                      ({formData.message.length}/160 characters)
                    </span>
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Enter your health education message here..."
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={160}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Keep messages concise and actionable.</p>
                </div>

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={loading}
                  disabled={selectedPatients.length === 0}
                  className="w-full"
                >
                  <IconSend className="h-5 w-5 mr-2" />
                  IconSend SMS Campaign
                  {selectedPatients.length > 0 && ` (${selectedPatients.length} patients)`}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Tips</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="flex items-start space-x-3">
                <IconMessage className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Keep it short</p>
                  <p className="text-gray-600">SMS is limited to 160 characters</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <IconPatients className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">Patient database</p>
                  <p className="text-gray-600">
                    Filter by diagnosis to target the right patients
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Message Examples</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-gray-700">
                  "Preeclampsia warning signs: High BP, headaches, vision changes. See a doctor immediately if pregnant with these symptoms. Your health matters!"
                </p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-gray-700">
                  "Mental health is health. It's okay to seek help. Talk to someone you trust or call our helpline. You're not alone."
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SMSCampaign;
